import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faTimes,
  faSave,
  faUser,
  faEnvelope,
  faPhone,
  faMobile,
  faUserTag,
  faEye,
  faEyeSlash,
  faKey,
} from '@fortawesome/free-solid-svg-icons';
import { UI_COLORS, colorHelpers } from '../../utils/colors';
import { formatNumericInput, isValidEmail, isValidPhoneNumber, validatePasswordStrength } from '../../utils/validate';
import SearchableSelect from '../common/SearchableSelect';
import warehouseService from '../../services/warehouseService';
import userService from '../../services/userService';
import useAuth from '../../hooks/useAuth';
import { useTranslation } from 'react-i18next';
import { extractApiError } from '../../utils/apiError';

const normalizeUserTypeToKey = (val, userTypes) => {
  const defaultType = userTypes[0]?.value || '';
  if (!val) return defaultType;
  if (userTypes.some((ut) => ut.value === val)) return val;
  const found = userTypes.find((ut) => ut.label === val);
  if (found) return found.value;
  return defaultType;
};

const RANK = {
  LABOUR: 1,
  DELIVERY_SUPERVISOR: 2,
  SALES_STAFF: 3,
  ACCOUNT_USER: 3,
  OPERATIONS_MANAGER: 4,
  ADMIN: 9,
  SUPER_ADMIN: 10
};

/**
 * UserForm Component
 * Modal form for adding/editing users with validation
 */
const UserForm = ({
  isOpen,
  onClose,
  onSubmit,
  user = null,
  loading = false,
}) => {
  const { t } = useTranslation();
  const { currentUser } = useAuth();
  const currentUserRole = currentUser?.user_type || currentUser?.role;

  // t(..., { returnObjects: true }) returns a NEW array reference on every
  // render. Memoize it so downstream memos/effects don't re-run each render
  // (which would reset formData on every keystroke, making the form uneditable).
  const USER_TYPES = React.useMemo(
    () => t('users.forms.userTypes', { returnObjects: true }),
    [t]
  );
  const FALLBACK_SUBMIT_ERROR = t('users.forms.submitFallbackError');

  const filteredUserTypes = React.useMemo(() => {
    return USER_TYPES.filter(type => {
      if (!currentUserRole) return true;
      if (currentUserRole === 'SUPER_ADMIN') {
        return RANK[type.value] <= RANK[currentUserRole];
      }
      return RANK[type.value] < RANK[currentUserRole];
    });
  }, [currentUserRole, USER_TYPES]);

  // String constants for UI text
  const strings = t('users.forms', { returnObjects: true });
  const keepCurrentHint = strings.fields.password.hints.keepCurrent;
  const requiredIndicator = t('common.symbols.required');

  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone_number: '',
    mobile_number: '',
    user_type: '',
    password: '',
    confirm_password: '',
    default_warehouse_id: '',
    language: 'english',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [warehouses, setWarehouses] = useState([]);
  const [selectedWarehouses, setSelectedWarehouses] = useState([]);
  const [loadingWarehouses, setLoadingWarehouses] = useState(false);

  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState('');
  // Local submit state so the form owns its own button/disabled lifecycle and is
  // never left "stuck" by the parent's shared actionLoading flag. Also guards
  // against double-submit.
  const [submitting, setSubmitting] = useState(false);
  const [resettingPassword, setResettingPassword] = useState(false);
  const [resetFeedback, setResetFeedback] = useState({ type: '', message: '' });

  // User types available in the system
  // value = backend enum key to send, label = display text
  // Initialize form data when user prop changes
  useEffect(() => {
    if (user) {
      setFormData({
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        email: user.email || '',
        phone_number: user.phone_number || '',
        mobile_number: user.mobile_number || '',
        user_type: normalizeUserTypeToKey(user.user_type, USER_TYPES),
        password: '',
        confirm_password: '',
        default_warehouse_id: user.default_warehouse_id || '',
        language: user.language || 'english',
      });
    } else {
      setFormData({
        first_name: '',
        last_name: '',
        email: '',
        phone_number: '',
        mobile_number: '',
        user_type: filteredUserTypes[0]?.value || '',
        password: '',
        confirm_password: '',
        default_warehouse_id: '',
        language: 'english',
      });
    }

    // Initialize selected warehouses if user exists and has them
    if (user && Array.isArray(user.warehouse_ids)) {
      setSelectedWarehouses(user.warehouse_ids.map(String));
    } else {
      setSelectedWarehouses([]);
    }

    setErrors({});
    setSubmitError('');
    setSubmitting(false); // defensively clear, so a reopened form is never stuck
    // Re-initialize only when the target user or open state changes — NOT when
    // filteredUserTypes/USER_TYPES change, or the form would reset mid-edit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, isOpen]);

  // Fetch warehouses
  useEffect(() => {
    if (!isOpen) return;

    const fetchWarehouses = async () => {
      setLoadingWarehouses(true);
      try {
        const res = await warehouseService.getAllWarehouses();
        if (res.success && Array.isArray(res.data)) {
          setWarehouses(res.data);
        }
      } catch (err) {
        console.error('Failed to fetch warehouses', err);
      } finally {
        setLoadingWarehouses(false);
      }
    };

    fetchWarehouses();
  }, [isOpen]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    let newValue = value;
    // For phone fields, keep digits only and enforce max 10 digits
    if (name === 'phone_number' || name === 'mobile_number') {
      newValue = formatNumericInput(value, { maxLength: 10 });
    }

    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : newValue
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
    // Clear the submit-level error once the user edits anything
    if (submitError) setSubmitError('');
  };

  const validateForm = () => {
    const newErrors = {};

    // Required fields
    if (!formData.first_name.trim()) {
      newErrors.first_name = strings.fields.firstName.errors.required;
    }

    if (!formData.last_name.trim()) {
      newErrors.last_name = strings.fields.lastName.errors.required;
    }

    if (!formData.email.trim()) {
      newErrors.email = strings.fields.email.errors.required;
    } else if (!isValidEmail(formData.email)) {
      newErrors.email = strings.fields.email.errors.invalid;
    }

    if (!formData.user_type) {
      newErrors.user_type = strings.fields.userType.errors.required;
    }

    // Phone validation (optional but if provided should be valid)
    // If provided, phone/mobile must be exactly 10 digits (max limit enforced on input)
    if (formData.phone_number) {
      if (!isValidPhoneNumber(formData.phone_number, { requiredLength: 10 })) {
        newErrors.phone_number = strings.fields.phoneNumber.errors.invalid;
      }
    }

    if (!formData.mobile_number) {
      if (!isValidPhoneNumber(formData.mobile_number, { requiredLength: 10 })) {
        newErrors.mobile_number = strings.fields.mobileNumber.errors.invalid;
      }
    }

    // Password validation: required when creating a new user (user prop is null)
    const creating = !user;
    // If creating, password is required and must match the policy.
    if (creating) {
      if (!formData.password) {
        newErrors.password = strings.fields.password.errors.required;
      } else if (!validatePasswordStrength(formData.password).isValid) {
        newErrors.password = strings.fields.password.errors.policy;
      }

      if (!formData.confirm_password) {
        newErrors.confirm_password = strings.fields.confirmPassword.errors.required;
      }
    } else {
      // On edit, only validate password rules if the user entered a new password
      if (formData.password) {
        if (!validatePasswordStrength(formData.password).isValid) {
          newErrors.password = strings.fields.password.errors.policy;
        }
      }
    }

    // If either password filled, ensure they match
    if ((formData.password || formData.confirm_password) && formData.password !== formData.confirm_password) {
      newErrors.confirm_password = strings.fields.confirmPassword.errors.mismatch;
    }

    // Default Warehouse validation
    if (!formData.default_warehouse_id) {
      newErrors.default_warehouse_id = strings.fields.defaultWarehouse.errors.required;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleResetPassword = async () => {
    const newErrors = {};
    if (!formData.password) {
      newErrors.password = strings.fields.password.errors.required;
    } else if (!validatePasswordStrength(formData.password).isValid) {
      newErrors.password = strings.fields.password.errors.policy;
    }

    if (!formData.confirm_password) {
      newErrors.confirm_password = strings.fields.confirmPassword.errors.required;
    } else if (formData.password !== formData.confirm_password) {
      newErrors.confirm_password = strings.fields.confirmPassword.errors.mismatch;
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setResettingPassword(true);
    setResetFeedback({ type: '', message: '' });

    try {
      const res = await userService.resetUserPassword(user.id, {
        new_password: formData.password,
        confirm_password: formData.confirm_password
      });

      if (res.success) {
        setResetFeedback({ type: 'success', message: res.message || t('users.forms.passwordReset.success') });
        setFormData(prev => ({ ...prev, password: '', confirm_password: '' }));
      } else {
        setResetFeedback({ type: 'error', message: res.error || t('users.forms.passwordReset.errorLabel') });
      }
    } catch (err) {
      setResetFeedback({ type: 'error', message: t('users.forms.passwordReset.unexpectedError') });
    } finally {
      setResettingPassword(false);
      // Clear feedback after 5 seconds
      setTimeout(() => setResetFeedback({ type: '', message: '' }), 5000);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Guard against double-submit (Enter key while already submitting, etc.).
    if (submitting) return;

    if (!validateForm()) return;

    // Build payload that backend expects
    const payload = {
      first_name: formData.first_name,
      last_name: formData.last_name,
      email: formData.email,
      phone_number: formData.phone_number,
      mobile_number: formData.mobile_number,
      user_type: formData.user_type, // send backend key like 'SALES_STAFF'
      warehouse_ids: selectedWarehouses.map(Number),
      default_warehouse_id: formData.default_warehouse_id ? Number(formData.default_warehouse_id) : null,
      language: formData.language || 'english',
    };

    // Include passwords if provided
    if (formData.password) payload.password = formData.password;
    if (formData.confirm_password) payload.confirm_password = formData.confirm_password;

    // Clear any previous submit error before retrying
    setSubmitError('');
    setSubmitting(true);

    // Await the parent submit handler. Only close on genuine success — on
    // failure OR an unexpected result shape, keep the form open with the entered
    // data and surface the error. `submitting` is always reset in finally so the
    // buttons can never get stuck disabled, whatever the parent returns/throws.
    try {
      const result = await onSubmit(payload);
      if (result && result.success) {
        onClose();
      } else {
        const rawErr = result && result.error;
        const msg = typeof rawErr === 'string' ? rawErr : (rawErr ? extractApiError(rawErr) : '');
        setSubmitError(msg || FALLBACK_SUBMIT_ERROR);
      }
    } catch (err) {
      setSubmitError(extractApiError(err) || FALLBACK_SUBMIT_ERROR);
    } finally {
      setSubmitting(false);
    }
  };

  const passwordLabel = user
    ? `${strings.fields.password.label} ${keepCurrentHint}`
    : `${strings.fields.password.label} ${requiredIndicator}`;
  const passwordPlaceholder = user
    ? strings.fields.password.placeholders.edit
    : strings.fields.password.placeholders.add;
  const confirmPasswordLabel = user
    ? strings.fields.confirmPassword.label
    : `${strings.fields.confirmPassword.label} ${requiredIndicator}`;
  const confirmPasswordPlaceholder = user
    ? strings.fields.confirmPassword.placeholders.edit
    : strings.fields.confirmPassword.placeholders.add;
  const submitLabel = user
    ? strings.buttons.submit.edit
    : strings.buttons.submit.add;
  const submitLoadingLabel = user
    ? strings.buttons.submit.loading.edit
    : strings.buttons.submit.loading.add;

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ backgroundColor: UI_COLORS.BACKGROUNDS.OVERLAY }}
    >
      {/* Hide scrollbars but keep scrolling functional */}
      <style>{`.user-form-modal{ -ms-overflow-style: none; scrollbar-width: none; } .user-form-modal::-webkit-scrollbar{ display: none; }`}</style>
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto user-form-modal"
        style={{ backgroundColor: UI_COLORS.BACKGROUNDS.CARD }}
      >
        {/* Header */}
        <div
          className="flex items-center justify-between p-6 border-b"
          style={{ borderColor: UI_COLORS.BORDERS.LIGHT }}
        >
          <h2
            className="text-2xl font-bold"
            style={{ color: UI_COLORS.TEXT.PRIMARY }}
          >
            {user ? strings.title.edit : strings.title.add}
          </h2>
          <button
            onClick={onClose}
            className="p-2 rounded-lg transition-all duration-200 hover:scale-105"
            style={{
              color: UI_COLORS.TEXT.MUTED,
              backgroundColor: colorHelpers.withOpacity(UI_COLORS.TEXT.MUTED, 0.1)
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = colorHelpers.withOpacity(UI_COLORS.TEXT.MUTED, 0.2);
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = colorHelpers.withOpacity(UI_COLORS.TEXT.MUTED, 0.1);
            }}
          >
            <FontAwesomeIcon icon={faTimes} className="text-lg" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Name Fields */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* First Name */}
            <div className="space-y-2">
              <label
                className="block text-sm font-semibold"
                style={{ color: UI_COLORS.FORMS.LABEL }}
              >
                <FontAwesomeIcon icon={faUser} className="mr-2" />
                {`${strings.fields.firstName.label} ${requiredIndicator}`}
              </label>
              <input
                type="text"
                name="first_name"
                value={formData.first_name}
                onChange={handleChange}
                className="w-full px-4 py-3 border-2 rounded-xl focus:ring-2 transition-all duration-200"
                style={{
                  backgroundColor: UI_COLORS.FORMS.INPUT.background,
                  borderColor: errors.first_name ? UI_COLORS.STATUS.ERROR : UI_COLORS.FORMS.INPUT.border,
                  color: UI_COLORS.FORMS.INPUT.text,
                  ...(errors.first_name && {
                    backgroundColor: colorHelpers.withOpacity(UI_COLORS.STATUS.ERROR, 0.1)
                  })
                }}
                placeholder={strings.fields.firstName.placeholder}
              />
              {errors.first_name && (
                <p
                  className="text-sm font-medium"
                  style={{ color: UI_COLORS.STATUS.ERROR }}
                >
                  {errors.first_name}
                </p>
              )}
            </div>

            {/* Last Name */}
            <div className="space-y-2">
              <label
                className="block text-sm font-semibold"
                style={{ color: UI_COLORS.FORMS.LABEL }}
              >
                <FontAwesomeIcon icon={faUser} className="mr-2" />
                {`${strings.fields.lastName.label} ${requiredIndicator}`}
              </label>
              <input
                type="text"
                name="last_name"
                value={formData.last_name}
                onChange={handleChange}
                className="w-full px-4 py-3 border-2 rounded-xl focus:ring-2 transition-all duration-200"
                style={{
                  backgroundColor: UI_COLORS.FORMS.INPUT.background,
                  borderColor: errors.last_name ? UI_COLORS.STATUS.ERROR : UI_COLORS.FORMS.INPUT.border,
                  color: UI_COLORS.FORMS.INPUT.text,
                  ...(errors.last_name && {
                    backgroundColor: colorHelpers.withOpacity(UI_COLORS.STATUS.ERROR, 0.1)
                  })
                }}
                placeholder={strings.fields.lastName.placeholder}
              />
              {errors.last_name && (
                <p
                  className="text-sm font-medium"
                  style={{ color: UI_COLORS.STATUS.ERROR }}
                >
                  {errors.last_name}
                </p>
              )}
            </div>
          </div>

          {/* Email */}
          <div className="space-y-2">
            <label
              className="block text-sm font-semibold"
              style={{ color: UI_COLORS.FORMS.LABEL }}
            >
              <FontAwesomeIcon icon={faEnvelope} className="mr-2" />
              {`${strings.fields.email.label} ${requiredIndicator}`}
            </label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              className="w-full px-4 py-3 border-2 rounded-xl focus:ring-2 transition-all duration-200"
              style={{
                backgroundColor: UI_COLORS.FORMS.INPUT.background,
                borderColor: errors.email ? UI_COLORS.STATUS.ERROR : UI_COLORS.FORMS.INPUT.border,
                color: UI_COLORS.FORMS.INPUT.text,
                ...(errors.email && {
                  backgroundColor: colorHelpers.withOpacity(UI_COLORS.STATUS.ERROR, 0.1)
                })
              }}
              placeholder={strings.fields.email.placeholder}
            />
            {errors.email && (
              <p
                className="text-sm font-medium"
                style={{ color: UI_COLORS.STATUS.ERROR }}
              >
                {errors.email}
              </p>
            )}
          </div>

          {/* Phone Numbers */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Mobile Number */}
            <div className="space-y-2">
              <label
                className="block text-sm font-semibold"
                style={{ color: UI_COLORS.FORMS.LABEL }}
              >
                <FontAwesomeIcon icon={faMobile} className="mr-2" />
                {`${strings.fields.mobileNumber.label} ${requiredIndicator}`}
              </label>
              <input
                type="tel"
                name="mobile_number"
                value={formData.mobile_number}
                onChange={handleChange}
                inputMode="numeric"
                maxLength={10}
                className="w-full px-4 py-3 border-2 rounded-xl focus:ring-2 transition-all duration-200"
                style={{
                  backgroundColor: UI_COLORS.FORMS.INPUT.background,
                  borderColor: errors.mobile_number ? UI_COLORS.STATUS.ERROR : UI_COLORS.FORMS.INPUT.border,
                  color: UI_COLORS.FORMS.INPUT.text,
                  ...(errors.mobile_number && {
                    backgroundColor: colorHelpers.withOpacity(UI_COLORS.STATUS.ERROR, 0.1)
                  })
                }}
                placeholder={strings.fields.mobileNumber.placeholder}
              />
              {errors.mobile_number && (
                <p
                  className="text-sm font-medium"
                  style={{ color: UI_COLORS.STATUS.ERROR }}
                >
                  {errors.mobile_number}
                </p>
              )}
            </div>

            {/* Phone Number */}
            <div className="space-y-2">
              <label
                className="block text-sm font-semibold"
                style={{ color: UI_COLORS.FORMS.LABEL }}
              >
                <FontAwesomeIcon icon={faPhone} className="mr-2" />
                {strings.fields.phoneNumber.label}
              </label>
              <input
                type="tel"
                name="phone_number"
                value={formData.phone_number}
                onChange={handleChange}
                inputMode="numeric"
                maxLength={10}
                className="w-full px-4 py-3 border-2 rounded-xl focus:ring-2 transition-all duration-200"
                style={{
                  backgroundColor: UI_COLORS.FORMS.INPUT.background,
                  borderColor: errors.phone_number ? UI_COLORS.STATUS.ERROR : UI_COLORS.FORMS.INPUT.border,
                  color: UI_COLORS.FORMS.INPUT.text,
                  ...(errors.phone_number && {
                    backgroundColor: colorHelpers.withOpacity(UI_COLORS.STATUS.ERROR, 0.1)
                  })
                }}
                placeholder={strings.fields.phoneNumber.placeholder}
              />
              {errors.phone_number && (
                <p
                  className="text-sm font-medium"
                  style={{ color: UI_COLORS.STATUS.ERROR }}
                >
                  {errors.phone_number}
                </p>
              )}
            </div>
          </div>

          {/* User Type */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <label
                className="block text-sm font-semibold"
                style={{ color: UI_COLORS.FORMS.LABEL }}
              >
                <FontAwesomeIcon icon={faUserTag} className="mr-2" />
                {`${strings.fields.userType.label} ${requiredIndicator}`}
              </label>
              <select
                name="user_type"
                value={formData.user_type}
                onChange={handleChange}
                className="w-full px-4 py-3 border-2 rounded-xl focus:ring-2 transition-all duration-200"
                style={{
                  backgroundColor: UI_COLORS.FORMS.INPUT.background,
                  borderColor: errors.user_type ? UI_COLORS.STATUS.ERROR : UI_COLORS.FORMS.INPUT.border,
                  color: UI_COLORS.FORMS.INPUT.text,
                  ...(errors.user_type && {
                    backgroundColor: colorHelpers.withOpacity(UI_COLORS.STATUS.ERROR, 0.1)
                  })
                }}
              >
                {filteredUserTypes.map(type => (
                  <option key={type.value} value={type.value}>{type.label}</option>
                ))}
              </select>
              {errors.user_type && (
                <p
                  className="text-sm font-medium"
                  style={{ color: UI_COLORS.STATUS.ERROR }}
                >
                  {errors.user_type}
                </p>
              )}
            </div>

            {/* Language */}
            <div className="space-y-2">
              <label
                className="block text-sm font-semibold"
                style={{ color: UI_COLORS.FORMS.LABEL }}
              >
                {strings.fields.language.label}
              </label>
              <select
                name="language"
                value={formData.language}
                onChange={handleChange}
                className="w-full px-4 py-3 border-2 rounded-xl focus:ring-2 transition-all duration-200"
                style={{
                  backgroundColor: UI_COLORS.FORMS.INPUT.background,
                  borderColor: UI_COLORS.FORMS.INPUT.border,
                  color: UI_COLORS.FORMS.INPUT.text,
                }}
              >
                <option value="english">{strings.fields.language.english}</option>
                <option value="hindi">{strings.fields.language.hindi}</option>
              </select>
            </div>
          </div>

          {/* Warehouses Section */}
          <div className="space-y-2">
            <label
              className="block text-sm font-semibold"
              style={{ color: UI_COLORS.FORMS.LABEL }}
            >
              {strings.fields.warehouses.label}
            </label>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 p-4 rounded-xl border-2 border-dashed" style={{ borderColor: UI_COLORS.BORDERS.LIGHT, backgroundColor: colorHelpers.withOpacity(UI_COLORS.BACKGROUNDS.MAIN, 0.5) }}>
              {/* Left Side: Dropdown */}
              <div>
                <label
                  className="block text-sm font-semibold mb-2"
                  style={{ color: UI_COLORS.FORMS.LABEL }}
                >
                  {strings.fields.warehouses.label}
                </label>
                <div className="relative">
                  <SearchableSelect
                    value=""
                    onChange={(e) => {
                      if (e.target.value) {
                        const val = String(e.target.value);
                        setSelectedWarehouses(prev => {
                          const current = prev.map(String);
                          return current.includes(val) ? current : [...current, val];
                        });
                      }
                    }}
                    options={warehouses
                      .filter(w => !selectedWarehouses.includes(String(w.id)))
                      .map(w => ({ value: w.id, label: w.alias_name || w.name || w.city }))
                    }
                    placeholder={strings.fields.warehouses.placeholder}
                    className="w-full"
                    disabled={loadingWarehouses}
                  />
                </div>
                <p className="text-xs mt-2" style={{ color: UI_COLORS.TEXT.SECONDARY }}>
                  {strings.fields.warehouses.helper}
                </p>
              </div>

              {/* Right Side: Selected Warehouses */}
              <div>
                <label className="block text-sm font-semibold mb-2" style={{ color: UI_COLORS.FORMS.LABEL }}>
                  {strings.fields.warehouses.label} ({selectedWarehouses.length})
                </label>
                <div className="min-h-[100px] p-3 rounded-xl border-2 bg-white" style={{ borderColor: UI_COLORS.FORMS.INPUT.border }}>
                  {selectedWarehouses.length === 0 ? (
                    <p className="text-sm text-center py-8 italic" style={{ color: UI_COLORS.TEXT.MUTED }}>{strings.fields.warehouses.empty}</p>
                  ) : (
                    <div className="flex flex-wrap gap-2">
                      {selectedWarehouses.map(id => {
                        const warehouse = warehouses.find(w => String(w.id) === String(id));
                        // If warehouse not loaded yet but id exists (e.g. from user prop), show ID or generic
                        const dispName = warehouse ? (warehouse.alias_name || warehouse.name || warehouse.city) : t('users.forms.warehouseFallback', { id });
                        return (
                          <div
                            key={id}
                            className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium shadow-sm"
                            style={{
                              backgroundColor: colorHelpers.withOpacity(UI_COLORS.BRAND.PRIMARY, 0.1),
                              color: '#000000',
                              border: '1px solid #000000'
                            }}
                          >
                            <span>{dispName}</span>
                            <button
                              type="button"
                              onClick={() => setSelectedWarehouses(prev => prev.filter(pid => String(pid) !== String(id)))}
                              className="hover:text-red-500 transition-colors"
                              title={t('users.forms.removeWarehouse')}
                            >
                              <FontAwesomeIcon icon={faTimes} />
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Default Warehouse Select */}
          <div className="space-y-2">
            <label
              className="block text-sm font-semibold"
              style={{ color: UI_COLORS.FORMS.LABEL }}
            >
              {`${strings.fields.defaultWarehouse.label} ${requiredIndicator}`}
            </label>
            <div className="w-full md:w-1/2">
              <SearchableSelect
                name="default_warehouse_id"
                value={formData.default_warehouse_id}
                onChange={handleChange}
                options={warehouses.map(w => ({
                  value: w.id,
                  label: w.alias_name || w.name || w.city
                }))}
                placeholder={strings.fields.defaultWarehouse.placeholder}
                style={{
                  borderColor: errors.default_warehouse_id ? UI_COLORS.STATUS.ERROR : undefined
                }}
              />
              {errors.default_warehouse_id && (
                <p className="text-sm font-medium mt-1" style={{ color: UI_COLORS.STATUS.ERROR }}>
                  {errors.default_warehouse_id}
                </p>
              )}
            </div>
            <p className="text-xs" style={{ color: UI_COLORS.TEXT.SECONDARY }}>
              {strings.fields.defaultWarehouse.helper}
            </p>
          </div>

          {/* Password Fields */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <label
                className="block text-sm font-semibold"
                style={{ color: UI_COLORS.FORMS.LABEL }}
              >
                {passwordLabel}
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border-2 rounded-xl focus:ring-2 transition-all duration-200"
                  style={{
                    backgroundColor: UI_COLORS.FORMS.INPUT.background,
                    borderColor: errors.password ? UI_COLORS.STATUS.ERROR : UI_COLORS.FORMS.INPUT.border,
                    color: UI_COLORS.FORMS.INPUT.text,
                    ...(errors.password && {
                      backgroundColor: colorHelpers.withOpacity(UI_COLORS.STATUS.ERROR, 0.1)
                    })
                  }}
                  placeholder={passwordPlaceholder}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(prev => !prev)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-2 text-sm text-gray-500"
                  aria-label={showPassword ? strings.fields.password.aria.hide : strings.fields.password.aria.show}
                >
                  <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} />
                </button>
              </div>
              {errors.password && (
                <p
                  className="text-sm font-medium"
                  style={{ color: UI_COLORS.STATUS.ERROR }}
                >
                  {errors.password}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <label
                className="block text-sm font-semibold"
                style={{ color: UI_COLORS.FORMS.LABEL }}
              >
                {confirmPasswordLabel}
              </label>
              <div className="relative">
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  name="confirm_password"
                  value={formData.confirm_password}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border-2 rounded-xl focus:ring-2 transition-all duration-200"
                  style={{
                    backgroundColor: UI_COLORS.FORMS.INPUT.background,
                    borderColor: errors.confirm_password ? UI_COLORS.STATUS.ERROR : UI_COLORS.FORMS.INPUT.border,
                    color: UI_COLORS.FORMS.INPUT.text,
                    ...(errors.confirm_password && {
                      backgroundColor: colorHelpers.withOpacity(UI_COLORS.STATUS.ERROR, 0.1)
                    })
                  }}
                  placeholder={confirmPasswordPlaceholder}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(prev => !prev)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-2 text-sm text-gray-500"
                  aria-label={showConfirmPassword ? strings.fields.confirmPassword.aria.hide : strings.fields.confirmPassword.aria.show}
                >
                  <FontAwesomeIcon icon={showConfirmPassword ? faEyeSlash : faEye} />
                </button>
              </div>
              {errors.confirm_password && (
                <p
                  className="text-sm font-medium"
                  style={{ color: UI_COLORS.STATUS.ERROR }}
                >
                  {errors.confirm_password}
                </p>
              )}
            </div>
          </div>

          {/* Password Reset Action (Only on Edit) */}
          {user && (
            <div className="flex flex-col space-y-3 p-4 rounded-xl border-2 border-dashed" style={{ borderColor: UI_COLORS.BORDERS.LIGHT, backgroundColor: colorHelpers.withOpacity(UI_COLORS.BACKGROUNDS.MAIN, 0.3) }}>
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="font-bold text-sm" style={{ color: UI_COLORS.TEXT.PRIMARY }}>{t('profile.passwordReset.heading')}</h4>
                  <p className="text-xs text-gray-500">{t('users.forms.passwordReset.helper')}</p>
                </div>
                <button
                  type="button"
                  onClick={handleResetPassword}
                  disabled={resettingPassword || submitting}
                  className="flex items-center space-x-2 px-4 py-2 rounded-lg font-semibold transition-all duration-200 hover:scale-105 disabled:opacity-50 disabled:transform-none"
                  style={{
                    backgroundColor: resettingPassword ? UI_COLORS.STATUS.SUCCESS : UI_COLORS.BRAND.PRIMARY,
                    color: '#000000',
                    border: '1px solid #000000'
                  }}
                >
                  {resettingPassword ? (
                    <div className="flex items-center space-x-2">
                      <div className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin"></div>
                      <span>{t('users.forms.passwordReset.resetting')}</span>
                    </div>
                  ) : (
                    <>
                      <FontAwesomeIcon icon={faKey} />
                      <span>{t('users.forms.passwordReset.submit')}</span>
                    </>
                  )}
                </button>
              </div>

              {resetFeedback.message && (
                <div
                  className={`text-sm font-medium p-2 rounded-lg text-center transition-all duration-500`}
                  style={{
                    backgroundColor: resetFeedback.type === 'success' ? colorHelpers.withOpacity(UI_COLORS.STATUS.SUCCESS, 0.1) : colorHelpers.withOpacity(UI_COLORS.STATUS.ERROR, 0.1),
                    color: resetFeedback.type === 'success' ? UI_COLORS.STATUS.SUCCESS : UI_COLORS.STATUS.ERROR
                  }}
                >
                  {resetFeedback.message}
                </div>
              )}
            </div>
          )}

          {/* Submit error banner — shown when the API call fails; form stays open with data intact */}
          {submitError && (
            <div
              className="rounded-xl px-4 py-3 text-sm font-medium"
              role="alert"
              style={{
                backgroundColor: colorHelpers.withOpacity(UI_COLORS.STATUS.ERROR, 0.08),
                color: UI_COLORS.STATUS.ERROR,
                border: `1px solid ${colorHelpers.withOpacity(UI_COLORS.STATUS.ERROR, 0.3)}`,
              }}
            >
              {submitError}
            </div>
          )}

          {/* Form Actions — the form's own `submitting` drives button state and
              always resets in finally, so buttons can't be left stuck by
              external/parent state. */}
          <div className="flex items-center justify-end space-x-4 pt-6">
            {/* Cancel Button */}
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              className="px-6 py-3 rounded-xl font-semibold transition-all duration-200 hover:scale-105 disabled:opacity-50 disabled:transform-none"
              style={{
                backgroundColor: 'transparent',
                color: UI_COLORS.TEXT.MUTED,
                border: `2px solid ${UI_COLORS.BORDERS.MEDIUM}`
              }}
              onMouseEnter={(e) => {
                if (!submitting) {
                  e.target.style.backgroundColor = colorHelpers.withOpacity(UI_COLORS.TEXT.MUTED, 0.1);
                }
              }}
              onMouseLeave={(e) => {
                if (!submitting) {
                  e.target.style.backgroundColor = 'transparent';
                }
              }}
            >
              {strings.buttons.cancel}
            </button>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={submitting}
              className="px-6 py-3 rounded-xl font-semibold transition-all duration-200 hover:scale-105 disabled:opacity-50 disabled:transform-none"
              style={{
                backgroundColor: submitting
                  ? UI_COLORS.BUTTONS.PRIMARY.loading.background
                  : UI_COLORS.BUTTONS.PRIMARY.background,
                color: submitting
                  ? UI_COLORS.BUTTONS.PRIMARY.loading.text
                  : UI_COLORS.BUTTONS.PRIMARY.text
              }}
            >
              {submitting ? (
                <div className="flex items-center space-x-2">
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  <span>{submitLoadingLabel}</span>
                </div>
              ) : (
                <div className="flex items-center space-x-2">
                  <FontAwesomeIcon icon={faSave} />
                  <span>{submitLabel}</span>
                </div>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default UserForm;